import { useRef, useState } from 'react';
import { CloseOutlined, HistoryOutlined, PaperClipOutlined, SendOutlined } from '@ant-design/icons';
import { Segmented, Slider } from 'antd';

import { Button, Skeleton, Textarea } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import type { KhoaDich } from '../../../../i18n';
import { ngayGioVN } from '../../../../lib/dinh-dang';
import { NHAN_TRANG_THAI, NHAN_TRUONG, NHAN_VAI_TRO } from '../../constants';
import { useDongThoiGian, useTaiTepCapNhat, useThemCapNhat } from '../../hooks/useNhiemVu';
import { BYTE_MOI_MB, docJson, TOI_DA_TEP_MB } from '../../nghiep-vu/nhiem-vu';
import type { HoatDong, NutNhiemVu, VaiTroThamGia } from '../../types-nhiem-vu';
import { UserAvatar } from './UserAvatar';

type CheDo = 'PROGRESS' | 'COMMENT';

const BUOC_TIEN_DO = 5;
const SO_DONG_CHO = 3;
const NOI_DUNG_TOI_DA = 5000;

/** Dòng thời gian của một nhiệm vụ kèm ô báo tiến độ, gửi ý kiến (`canWrite` = quyền tạo). */
export function TaskActivityPanel({ task, canWrite }: { task: NutNhiemVu; canWrite: boolean }) {
  const { t } = useT();
  const ds = useDongThoiGian(task.id);
  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-secondary">
        {t('vanBanNhiemVu.hoatDong.tieuDe')}
      </h3>
      {canWrite && <Composer key={task.id} task={task} />}
      <div className="mt-4">
        {ds.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: SO_DONG_CHO }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : ds.isError ? (
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-danger-700">{t('vanBanNhiemVu.hoatDong.loi')}</p>
            <Button size="sm" variant="outline" onClick={() => void ds.refetch()}>
              {t('chung.thuLai')}
            </Button>
          </div>
        ) : (ds.data ?? []).length === 0 ? (
          <p className="text-sm text-content-muted">{t('vanBanNhiemVu.hoatDong.rong')}</p>
        ) : (
          <ol className="space-y-4">
            {(ds.data ?? []).map((a) => (
              <ActivityItem key={a.id} taskId={task.id} activity={a} />
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}

function Composer({ task }: { task: NutNhiemVu }) {
  const { t } = useT();
  const add = useThemCapNhat();
  const fileRef = useRef<HTMLInputElement>(null);
  // Việc có việc con thì tiến độ suy ra từ các việc con, không tự báo được — chỉ còn gửi ý kiến.
  const baoDuocTienDo = task.child_total === 0;
  const [mode, setMode] = useState<CheDo>(baoDuocTienDo ? 'PROGRESS' : 'COMMENT');
  const [progress, setProgress] = useState(task.progress);
  const [content, setContent] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loi, setLoi] = useState<string | null>(null);

  const chonTep = (f: File | null) => {
    if (f && f.size > TOI_DA_TEP_MB * BYTE_MOI_MB) {
      setLoi(t('vanBanNhiemVu.form.tepQuaLon', { so: TOI_DA_TEP_MB }));
      return;
    }
    setLoi(null);
    setFile(f);
  };

  const gui = () => {
    if (mode === 'COMMENT' && !content.trim() && !file) {
      setLoi(t('vanBanNhiemVu.hoatDong.yKienThieu'));
      return;
    }
    setLoi(null);
    add.mutate(
      { id: task.id, vao: { kind: mode, content, progress: mode === 'PROGRESS' ? progress : undefined, file } },
      {
        onSuccess: () => {
          setContent('');
          setFile(null);
        },
      },
    );
  };

  const nhanO = mode === 'PROGRESS' ? t('vanBanNhiemVu.hoatDong.ghiChuTienDo') : t('vanBanNhiemVu.hoatDong.yKienGoiY');

  return (
    <div className="space-y-3 rounded-lg border border-default p-3">
      {baoDuocTienDo && (
        <Segmented<CheDo>
          block
          value={mode}
          onChange={setMode}
          options={[
            { value: 'PROGRESS', label: t('vanBanNhiemVu.hoatDong.tabTienDo') },
            { value: 'COMMENT', label: t('vanBanNhiemVu.hoatDong.tabYKien') },
          ]}
        />
      )}
      {mode === 'PROGRESS' && (
        <div>
          <div className="flex items-center justify-between text-xs text-content-secondary">
            <span>{t('vanBanNhiemVu.hoatDong.tienDoHienTai')}</span>
            <span className="font-semibold tabular-nums text-content-primary">{progress}%</span>
          </div>
          <Slider
            min={0}
            max={100}
            step={BUOC_TIEN_DO}
            value={progress}
            onChange={setProgress}
            aria-label={t('vanBanNhiemVu.hoatDong.tienDoHienTai')}
          />
          {task.status === 'MOI_PHAN_CONG' && (
            <p className="text-2xs text-content-muted">{t('vanBanNhiemVu.hoatDong.tienDoMo')}</p>
          )}
        </div>
      )}
      <Textarea
        autoSize={{ minRows: 2, maxRows: 6 }}
        maxLength={NOI_DUNG_TOI_DA}
        value={content}
        aria-label={nhanO}
        onChange={(e) => setContent(e.target.value)}
        placeholder={nhanO}
      />
      {loi && (
        <p role="alert" className="text-xs text-danger-600">
          {loi}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            chonTep(e.target.files?.[0] ?? null);
            e.target.value = '';
          }}
        />
        {file ? (
          <span className="inline-flex min-w-0 max-w-full items-center gap-1 rounded bg-subtle px-2 py-1 text-xs text-content-secondary sm:max-w-xs">
            <PaperClipOutlined aria-hidden />
            <span className="truncate">{file.name}</span>
            <button
              type="button"
              aria-label={t('vanBanNhiemVu.hoatDong.boTep', { ten: file.name })}
              onClick={() => setFile(null)}
              className="text-content-muted hover:text-content-primary"
            >
              <CloseOutlined className="text-2xs" />
            </button>
          </span>
        ) : (
          <Button variant="ghost" size="sm" icon={<PaperClipOutlined />} onClick={() => fileRef.current?.click()}>
            {t('vanBanNhiemVu.hoatDong.dinhKem')}
          </Button>
        )}
        <Button className="ml-auto" variant="primary" size="sm" icon={<SendOutlined />} loading={add.isPending} onClick={gui}>
          {add.isPending
            ? t('vanBanNhiemVu.hoatDong.dangGui')
            : mode === 'PROGRESS'
              ? t('vanBanNhiemVu.hoatDong.nutTienDo')
              : t('vanBanNhiemVu.hoatDong.nutYKien')}
        </Button>
      </div>
    </div>
  );
}

interface ThayDoiNguoi {
  name: string;
  role: string;
}

function ActivityItem({ taskId, activity: a }: { taskId: string; activity: HoatDong }) {
  const { t, locale } = useT();
  const tai = useTaiTepCapNhat();
  const actor = a.actor_name ?? t('vanBanNhiemVu.hoatDong.heThong');
  const ghiChuNguoi = a.kind === 'COMMENT' || a.kind === 'PROGRESS';

  return (
    <li>
      <div className="flex items-start gap-2">
        {ghiChuNguoi ? (
          <UserAvatar name={actor} className="h-6 w-6" />
        ) : (
          <span
            aria-hidden
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-subtle text-2xs text-content-muted"
          >
            <HistoryOutlined />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm text-content-secondary">
            <span className="font-medium text-content-primary">{actor}</span> <ActivitySummary activity={a} />
          </p>
          <ActivityDetail activity={a} />
          {a.content && ghiChuNguoi && (
            <p className="mt-1 whitespace-pre-line rounded-lg bg-subtle px-3 py-2 text-sm text-content-primary">
              {a.content}
            </p>
          )}
          {a.attachment_filename && (
            <button
              type="button"
              onClick={() => tai.mutate({ taskId, hd: a })}
              className="mt-1 inline-flex max-w-full items-center gap-1 text-xs text-content-link hover:underline"
            >
              <PaperClipOutlined aria-hidden />
              <span className="truncate">{a.attachment_filename}</span>
            </button>
          )}
          <p className="mt-0.5 text-2xs tabular-nums text-content-muted">{ngayGioVN(a.created_at, locale)}</p>
        </div>
      </div>
    </li>
  );
}

const laVaiTro = (r: string): r is VaiTroThamGia => r === 'CHU_TRI' || r === 'PHOI_HOP' || r === 'XEM_DE_BIET';
const khoaLoai = (kind: string) => `vanBanNhiemVu.loaiHoatDong.${kind}` as KhoaDich;

/** Câu mô tả một hoạt động, dùng chung cho dòng thời gian và thông báo. */
export function ActivitySummary({ activity: a }: { activity: HoatDong }) {
  const { t } = useT();
  switch (a.kind) {
    case 'STATUS_CHANGED':
      return (
        <>
          {t(khoaLoai(a.actor_id ? 'STATUS_CHANGED' : 'STATUS_CHANGED_AUTO'), {
            tu: a.from_status ? t(NHAN_TRANG_THAI[a.from_status]) : '',
            sang: a.to_status ? t(NHAN_TRANG_THAI[a.to_status]) : '',
          })}
        </>
      );
    case 'PROGRESS':
      return <>{t(khoaLoai('PROGRESS'), { phanTram: a.progress ?? 0 })}</>;
    case 'CHILD_ADDED':
      return <>{t(khoaLoai('CHILD_ADDED'), { ten: docJson<{ title: string }>(a.content)?.title ?? '' })}</>;
    case 'UPDATED': {
      const fields = docJson<{ fields: string[] }>(a.content)?.fields ?? [];
      const truong = fields.map((f) => (NHAN_TRUONG[f] ? t(NHAN_TRUONG[f]) : f)).join(', ');
      return <>{t(khoaLoai('UPDATED'), { truong })}</>;
    }
    default:
      return <>{t(khoaLoai(a.kind))}</>;
  }
}

function ActivityDetail({ activity: a }: { activity: HoatDong }) {
  const { t } = useT();
  if (a.kind === 'PROGRESS' && a.progress !== null) {
    return (
      <div className="mt-1 h-1.5 w-40 overflow-hidden rounded-full bg-subtle">
        <div className="h-full rounded-full bg-primary-500" style={{ width: `${a.progress}%` }} />
      </div>
    );
  }
  if (a.kind !== 'PARTICIPANTS_CHANGED') return null;
  const diff = docJson<{ added?: ThayDoiNguoi[]; removed?: ThayDoiNguoi[]; role_changed?: ThayDoiNguoi[] }>(a.content);
  if (!diff) return null;
  const vaiTro = (r: string) => (laVaiTro(r) ? t(NHAN_VAI_TRO[r]) : r);
  const dong = [
    ...(diff.added ?? []).map((p) => t('vanBanNhiemVu.hoatDong.themNguoi', { ten: p.name, vaiTro: vaiTro(p.role) })),
    ...(diff.removed ?? []).map((p) => t('vanBanNhiemVu.hoatDong.boNguoi', { ten: p.name, vaiTro: vaiTro(p.role) })),
    ...(diff.role_changed ?? []).map((p) =>
      t('vanBanNhiemVu.hoatDong.doiVaiTro', { ten: p.name, vaiTro: vaiTro(p.role) }),
    ),
  ];
  return (
    <ul className="mt-1 space-y-0.5 text-xs text-content-muted">
      {dong.map((d, i) => (
        <li key={i}>{d}</li>
      ))}
    </ul>
  );
}
